import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, MOBILE, SPACE, STATUS_COLORS } from '@/nova/theme/tokens';

import { ReviewBadge } from '../BannerKitComposer/BannerKitComposer.styles';
import { lockedRailPassThrough } from '../NewVersionLink';
import { ChipRow, PreviewNote, RailScroll } from '../PlayableComposer/PlayableComposer.styles';
import { BROWSER_BAR_H, FRAME_EDGE } from './preview-layout';

/* The landing page composer: the mini-game composer's frame (header; the template step; left, the Art Style page's
   ingredient sections over a pinned foot; right, the output) and its preview notes, so the Marketing editors read as
   one system. Its own pieces: the locked rail, the subject cards, the template line over the preview, the offer
   options, the hero pick, the scene layers and the measured preview stage with its width toggle. */

export {
  ArtItem,
  ArtLabel,
  ArtStrip,
  ArtStyleBody,
  ArtThumb,
  CopyHeadline,
  CopySub,
  FieldError,
  FootText,
  Header,
  HeaderLeft,
  Hint,
  KitOutput,
  KitRail,
  LockNote,
  NoteText,
  OutputScroll,
  PickerActions,
  PickerHead,
  PickerIntro,
  PickerWrap,
  PreviewNote,
  ProgressFill,
  ProgressTrack,
  RailFoot,
  RailScroll,
  RailStatus,
  Section,
  SectionTitle,
  SelectedMark,
  Spinner,
  Split,
  SubjectAction,
  SubjectCard,
  SubjectCopy,
  SubjectThumb,
  TemplateCard,
  TemplateGrid,
  TemplateName,
  TemplateText,
  TitleEditIcon,
  TitleInput,
  TitleWrap,
} from '../PlayableComposer/PlayableComposer.styles';

/** The sheet's title (nothing to rename until a page can be saved). */
export const HeaderTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.text};
`;

/** A section body: its controls one under another. */
export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  min-width: 0;
`;

/** The rail's scrolling setup; on a generated (locked) version its disabled fields let clicks fall through to it
 *  (the "New version from this" link then shakes). */
export const LockableRailScroll = styled(RailScroll)<{ $locked: boolean }>`
  ${(p) => p.$locked && lockedRailPassThrough}
`;

/* ── Subject ── */

/** What the page features: one card per promotion or game. */
export const FeaturedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/** The subject's actions, under its card(s) on the right. */
export const SubjectActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${SPACE[8]};
  margin-top: -${SPACE[8]};
`;

/* ── Template line (over the preview) ── */

/** The page's template and "Change template" (the template step), over the preview. */
export const TemplateLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  color: ${COLORS.textMuted};
`;

export const TemplateLineText = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
`;

/* ── Offer ── */

export const OfferList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const OfferOption = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]} ${SPACE[12]};
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.text : COLORS.hairline)};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.text};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

export const OfferName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OfferTag = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

/* ── Scene (templates with a scene) ── */

/** The scene's layers as small tiles, wrapping. */
export const SceneGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
  gap: ${SPACE[8]};
`;

/** One layer: its art (or a "not generated yet" tile) over its name; a press picks it for a URL replacement. */
export const SceneSlot = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
  padding: ${SPACE[4]};
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.text : 'transparent')};
  background: transparent;
  color: ${COLORS.text};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
  }
  &:disabled {
    cursor: default;
  }
`;

/** A read-only layer tile (the symbols). */
export const SceneTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
  padding: ${SPACE[4]};
`;

export const SceneThumb = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  display: block;
`;

export const ScenePlaceholder = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  padding: ${SPACE[4]};
  border-radius: 8px;
  border: 1px dashed ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  text-align: center;
`;

export const ScenePlaceholderText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 10px;
  line-height: 1.25;
  color: ${COLORS.textFaint};
`;

export const SceneLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SceneGroupLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

/* ── Control groups ── */

/** A control group's head: its label, and an action on the right. */
export const GroupHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[8]};
  min-height: 28px;
`;

export const GroupLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

/* ── Scene characters (the cast) ── */

/** Automatic and No characters, side by side. */
export const CastModes = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${SPACE[8]};
`;

export const CastMode = styled(OfferOption)`
  justify-content: flex-start;
`;

/** The cast's mascots as cards, wrapping. */
export const CastGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: ${SPACE[8]};
`;

/** One mascot to feature: its image over its name; a picked one carries its side of the game. */
export const CastCard = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
  padding: ${SPACE[4]};
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.text : COLORS.hairline)};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.text};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
  }
  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

export const CastName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** A picked mascot's side ("Left", "Right"), pinned over its image's corner. */
export const CastSide = styled(Text).attrs({ tag: 'span' as const })`
  position: absolute;
  top: ${SPACE[8]};
  left: ${SPACE[8]};
  padding: 2px ${SPACE[8]};
  border-radius: 999px;
  background: ${COLORS.text};
  color: ${COLORS.surfaceCard};
  font-size: 11px;
  font-weight: 700;
`;

/* ── Hero ── */

export const HeroThumb = styled.img`
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  display: block;
`;

/** The generated hero's vision check did not pass it (the banner kit's size-tile badge, sized to its text). */
export const HeroReviewBadge = styled(ReviewBadge)`
  align-self: flex-start;
`;

/* ── Preview ── */

/** The preview head: the width toggle and Replay on the left, the publish controls on the right. */
export const PreviewHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

export const PreviewHeadSide = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/** The width toggle as labelled chips: wider screens only (a phone gets the icon pill below). */
export const WidthChips = styled(ChipRow)`
  ${MOBILE} {
    display: none;
  }
`;

/** The width toggle on a phone: the studio's device pill (touch size), in place of the chips. */
export const PhoneDevicePill = styled.div`
  display: none;

  ${MOBILE} {
    display: inline-flex;
  }
`;

export const PreviewStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

/** The measured stage the frame is fitted into: it takes the pane's remaining height (its own content never grows it,
 *  so the frame sized from it cannot feed back into it). It draws nothing itself: the frame spans its width and hugs
 *  the page's height, so a pane taller than the page leaves plain pane below the frame, not bars around the page. */
export const Stage = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 360px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  ${MOBILE} {
    flex: 0 0 auto;
    height: 560px;
  }
`;

/** The preview's frame: the mock browser bar over the page, the page centred in it (a phone shows the backdrop on its
 *  sides). */
export const FrameCard = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: ${FRAME_EDGE}px solid ${COLORS.hairline};
  border-radius: 14px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
`;

/** The page's box under the bar, as tall as the page is shown. */
export const FrameViewport = styled.div<{ $h: number }>`
  position: relative;
  flex: 0 0 auto;
  height: ${(p) => p.$h}px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: hidden;
  background: ${COLORS.surfaceInset};

  /* The page meets the bar edge to edge: the preview box's own rounding is dropped (the frame rounds the corners). */
  & > * {
    border-radius: 0;
  }
`;

/** A note in place of the page (waiting for a template, building the first preview). */
export const FrameNote = styled(PreviewNote)`
  flex: 1 1 auto;
  align-self: stretch;
`;

/* ── The mock browser bar ── */

/** The bar's height is the layout's (the frame is sized around it); a size container, so a narrow bar drops its
 *  dots and its state word. */
export const BrowserBar = styled.div`
  flex: 0 0 auto;
  height: ${BROWSER_BAR_H}px;
  container-type: inline-size;
  border-bottom: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
`;

export const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  height: 100%;
  padding: 0 ${SPACE[12]};
`;

export const BarDots = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  gap: ${SPACE[4]};

  /* Layout: the dots go first when the bar is narrow. */
  @container (max-width: 480px) {
    display: none;
  }
`;

export const BarDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${COLORS.surfaceMuted};
`;

/** The address: a lock, the landing host (muted), the path as an inline field and a copy button. A press anywhere on
 *  it edits the path. */
export const AddressPill = styled.label<{ $invalid: boolean; $disabled: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 560px;
  margin: 0 auto;
  /* Layout: a 28px pill sits centred in the 44px bar. */
  height: 28px;
  display: flex;
  align-items: center;
  gap: ${SPACE[4]};
  padding: 0 ${SPACE[2]} 0 ${SPACE[12]};
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$invalid ? STATUS_COLORS.negative : COLORS.hairline)};
  background: ${COLORS.surfaceMuted};
  cursor: ${(p) => (p.$disabled ? 'default' : 'text')};
  opacity: ${(p) => (p.$disabled ? 0.7 : 1)};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus-within {
    border-color: ${(p) => (p.$invalid ? STATUS_COLORS.negative : COLORS.textFaint)};
  }
`;

export const AddressHost = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: ${COLORS.textFaint};
`;

/** The path, typed in place right after the host. */
export const SlugInput = styled.input`
  flex: 1 1 0;
  /* Layout: room for a few characters however narrow the bar. */
  min-width: 56px;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 13px;
  color: ${COLORS.text};
  text-overflow: ellipsis;

  &::placeholder {
    color: ${COLORS.textFaint};
  }

  &:disabled {
    color: ${COLORS.textMuted};
    cursor: default;
  }
`;

export const AddressCopy = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
`;

/** The page's state at the bar's end: a dot and a word (the word goes when the bar is narrow). */
export const BarStatus = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

export const BarStatusText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  white-space: nowrap;

  /* Layout: the state word goes when the bar is narrow (the dot keeps it as its tooltip). */
  @container (max-width: 400px) {
    display: none;
  }
`;

/* ── Generate ── */

/** What the next Generate draws and writes, one checkbox each, above the button. */
export const OpsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[4]} ${SPACE[12]};
`;

/* ── Publish ── */

/** The preview head's right side: the publish pill and the more menu. */
export const PublishActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  margin-left: auto;
`;

/** Holds the pill's tooltip (a disabled button shows none of its own). */
export const PillWrap = styled.span`
  display: inline-flex;
`;

/** What publishing has to say, between the preview head and the frame. */
export const PublishNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/** Why the path in the bar cannot be published at (the path field names it). */
export const PathProblem = styled.div`
  display: flex;
`;

export const PubDot = styled.span<{ $tone: 'live' | 'off' | 'failed' }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${(p) =>
    p.$tone === 'live' ? STATUS_COLORS.positive : p.$tone === 'failed' ? STATUS_COLORS.negative : COLORS.textFaint};
`;

export const ConfirmBody = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 14px;
  color: ${COLORS.textMuted};
  overflow-wrap: anywhere;
`;

export const ConfirmFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${SPACE[8]};
`;

/** The preview's area in the scrolling output pane: it fills the pane's height, never under 640px (the tracking link,
 *  once live, follows it and the pane scrolls). Layout. */
export const PreviewArea = styled.div`
  flex: 1 0 auto;
  min-height: 640px;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  min-width: 0;

  ${MOBILE} {
    flex: 0 0 auto;
    min-height: 0;
  }
`;
